/**
 * benchmarking.js
 * Q25 · Build-38 — Industry Benchmarking Tool
 * ──────────────────────────────────────────────────────────
 * Architecture:
 *   - Client-side benchmarking using embedded benchmark data
 *     (no network call needed; data in benchmarks.json baked inline)
 *   - Also calls POST /api/benchmarking worker endpoint when available
 *     (returns server-side percentile calculation with more precision)
 *   - Graceful fallback: client-side percentile estimation if worker unavailable
 * ──────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  const WORKER_BASE = document.getElementById('bm-app')?.dataset.workerBase || '';

  // ── Embedded benchmark data (subset of benchmarks.json) ───────
  // Full dataset in assets/benchmarks.json. Loaded inline here for zero-dep operation.
  const BENCHMARKS = {
    bid_rate: {
      NE: { low: 0.095, mid: 0.115, high: 0.145, wage: 20.82 },
      SE: { low: 0.065, mid: 0.082, high: 0.105, wage: 14.95 },
      MW: { low: 0.072, mid: 0.090, high: 0.115, wage: 16.34 },
      SW: { low: 0.068, mid: 0.085, high: 0.108, wage: 15.60 },
      W:  { low: 0.088, mid: 0.110, high: 0.142, wage: 19.55 }
    },
    win_rate: {
      micro: { median: 32, range: [24, 42] },
      small: { median: 28, range: [20, 38] },
      mid:   { median: 25, range: [18, 34] },
      large: { median: 22, range: [15, 30] }
    },
    margin: {
      office:     { mid: 28, range: [22, 35] },
      medical:    { mid: 24, range: [18, 30] },
      school:     { mid: 26, range: [20, 34] },
      industrial: { mid: 32, range: [25, 42] },
      retail:     { mid: 27, range: [20, 35] }
    }
  };

  const SOURCE_LABELS = {
    bls_oews_2024:  { name: 'BLS OEWS 2024', url: 'https://www.bls.gov/oes/current/oes372011.htm' },
    issa_cims_2024: { name: 'ISSA CIMS 2024', url: 'https://www.issa.com/standards/cims' },
    bscai_2024:     { name: 'BSCAI Survey 2024', url: 'https://www.bscai.org/' },
    ibisworld_2025: { name: 'IBISWorld 2025', url: 'https://www.ibisworld.com/industry-statistics/profit-margin/janitorial-services-in-the-us/' }
  };

  const REGION_LABELS = {
    NE: 'Northeast', SE: 'Southeast', MW: 'Midwest', SW: 'Southwest', W: 'West'
  };
  const FACILITY_LABELS = {
    office: 'Office', medical: 'Medical/Clinical', school: 'School/Education',
    industrial: 'Industrial/Warehouse', retail: 'Retail'
  };
  const SIZE_LABELS = {
    micro: 'Micro (<5 employees)', small: 'Small (5–25)', mid: 'Mid (26–100)', large: 'Large (100+)'
  };

  // ── Percentile estimation ──────────────────────────────────────
  /**
   * Estimates percentile of `value` within a distribution described by low/mid/high.
   * Approximates using a simple linear interpolation across quartiles:
   *   low ≈ 25th pct, mid ≈ 50th, high ≈ 75th
   */
  function estimatePercentile(value, low, mid, high) {
    if (value <= low)  return Math.max(1, Math.round(25 * (value / low)));
    if (value <= mid)  return Math.round(25 + 25 * ((value - low) / (mid - low)));
    if (value <= high) return Math.round(50 + 25 * ((value - mid) / (high - mid)));
    return Math.min(99, Math.round(75 + 25 * ((value - high) / high)));
  }

  /**
   * Returns percentile for win rate vs benchmark range.
   * range[0] = ~10th pct, median = 50th, range[1] = ~90th
   */
  function winRatePercentile(value, median, range) {
    const p10 = range[0], p90 = range[1];
    if (value <= p10)     return Math.round(10 * (value / p10));
    if (value <= median)  return Math.round(10 + 40 * ((value - p10) / (median - p10)));
    if (value <= p90)     return Math.round(50 + 40 * ((value - median) / (p90 - median)));
    return Math.min(99, Math.round(90 + 9 * ((value - p90) / p90)));
  }

  // ── KPI Card builder ───────────────────────────────────────────
  function pctLabel(pct) {
    if (pct >= 75) return { text: 'Above average', cls: 'bm-kpi--above' };
    if (pct >= 45) return { text: 'At market', cls: 'bm-kpi--market' };
    if (pct >= 25) return { text: 'Below average', cls: 'bm-kpi--below' };
    return { text: 'Below industry floor', cls: 'bm-kpi--low' };
  }

  function buildKPICard({ title, yourValue, yourDisplay, benchmarkDisplay, percentile, insight, sources }) {
    const lbl = pctLabel(percentile);
    const barWidth = Math.min(100, Math.max(2, percentile));
    return `
      <div class="bm-kpi ${lbl.cls}">
        <div class="bm-kpi__header">
          <span class="bm-kpi__title">${title}</span>
          <span class="bm-kpi__badge bm-kpi__badge--${lbl.cls.replace('bm-kpi--','')}">${lbl.text}</span>
        </div>
        <div class="bm-kpi__values">
          <div class="bm-kpi__yours">
            <span class="bm-kpi__yours-label">Your number</span>
            <span class="bm-kpi__yours-val">${yourDisplay}</span>
          </div>
          <div class="bm-kpi__bench">
            <span class="bm-kpi__bench-label">Industry benchmark</span>
            <span class="bm-kpi__bench-val">${benchmarkDisplay}</span>
          </div>
        </div>
        <div class="bm-kpi__bar-wrap" aria-label="Percentile: ${percentile}%" role="img">
          <div class="bm-kpi__bar-track">
            <div class="bm-kpi__bar-fill" style="width:${barWidth}%"></div>
            <div class="bm-kpi__bar-marker" style="left:${barWidth}%">
              <span class="bm-kpi__bar-pct">${percentile}th pct</span>
            </div>
          </div>
          <div class="bm-kpi__bar-labels">
            <span>0%</span><span>50th</span><span>100%</span>
          </div>
        </div>
        <p class="bm-kpi__insight">${insight}</p>
        <div class="bm-kpi__sources">
          ${sources.map(s => `<a class="bm-cite" href="${SOURCE_LABELS[s]?.url || '#'}" target="_blank" rel="noopener">[${s.split('_')[0].toUpperCase()}]</a>`).join('')}
        </div>
      </div>
    `;
  }

  // ── Run analysis (client-side) ─────────────────────────────────
  function runClientAnalysis(inputs) {
    const { region, facility_type, company_size, bid_rate, win_rate, margin_pct } = inputs;
    const cards = [];

    // 1. Bid rate
    if (bid_rate !== null && bid_rate > 0) {
      const bm = BENCHMARKS.bid_rate[region];
      if (bm) {
        const pct = estimatePercentile(bid_rate, bm.low, bm.mid, bm.high);
        const dir = bid_rate < bm.low ? 'below the regional floor' :
                    bid_rate > bm.high ? 'above the regional ceiling' : 'within market range';
        cards.push(buildKPICard({
          title: '$/sqft Bid Rate',
          yourValue: bid_rate,
          yourDisplay: `$${bid_rate.toFixed(3)}/sqft/mo`,
          benchmarkDisplay: `$${bm.low.toFixed(3)}–$${bm.high.toFixed(3)}/sqft/mo (${REGION_LABELS[region]} median: $${bm.mid.toFixed(3)})`,
          percentile: pct,
          insight: `Your bid rate is ${dir} for the ${REGION_LABELS[region]} region. The regional median janitor wage is $${bm.wage}/hr (BLS OEWS 2024). ${bid_rate < bm.mid ? 'Consider whether your pricing covers your loaded labor cost.' : 'Your rate is competitive — ensure scope is clearly defined to justify it.'}`,
          sources: ['bls_oews_2024', 'bscai_2024']
        }));
      }
    }

    // 2. Win rate
    if (win_rate !== null && win_rate >= 0) {
      const bm = BENCHMARKS.win_rate[company_size];
      if (bm) {
        const pct = winRatePercentile(win_rate, bm.median, bm.range);
        cards.push(buildKPICard({
          title: 'Win Rate',
          yourValue: win_rate,
          yourDisplay: `${win_rate}%`,
          benchmarkDisplay: `${bm.median}% median for ${SIZE_LABELS[company_size]} companies (range: ${bm.range[0]}–${bm.range[1]}%)`,
          percentile: pct,
          insight: `${SIZE_LABELS[company_size]} contractors average a ${bm.median}% win rate (BSCAI 2024). ${win_rate < bm.range[0] ? 'Your win rate is below the industry low — review bid qualification criteria and proposal quality.' : win_rate > bm.range[1] ? 'Strong win rate. Ensure you\'re not underpricing to win — check margin on won jobs.' : 'Your win rate is in a healthy range for your company size.'}`,
          sources: ['bscai_2024', 'issa_cims_2024']
        }));
      }
    }

    // 3. Margin
    if (margin_pct !== null && margin_pct >= 0) {
      const bm = BENCHMARKS.margin[facility_type];
      if (bm) {
        const pct = estimatePercentile(margin_pct, bm.range[0], bm.mid, bm.range[1]);
        cards.push(buildKPICard({
          title: 'Gross Margin',
          yourValue: margin_pct,
          yourDisplay: `${margin_pct}%`,
          benchmarkDisplay: `${bm.mid}% average for ${FACILITY_LABELS[facility_type]} (range: ${bm.range[0]}–${bm.range[1]}%) — IBISWorld net: 8.1%`,
          percentile: pct,
          insight: `${FACILITY_LABELS[facility_type]} cleaning averages ${bm.mid}% gross margin (BSCAI 2024). Industry-wide net profit averages 8.1% (IBISWorld 2025). ${margin_pct < bm.range[0] ? 'Margin is below the industry floor — review labor hours, chemical dilution ratios, and job costing.' : margin_pct > bm.range[1] ? 'Strong margin. Verify scope and deliverables match your pricing to avoid client churn.' : 'Your margin is within a healthy range for this facility type.'}`,
          sources: ['ibisworld_2025', 'bscai_2024', 'issa_cims_2024']
        }));
      }
    }

    return cards;
  }

  // ── Render results ─────────────────────────────────────────────
  function renderResults(inputs, cards) {
    const resultsSection = document.getElementById('bm-results');
    const formSection    = document.querySelector('.bm-section--form');
    const kpiGrid        = document.getElementById('bm-kpi-grid');
    const context        = document.getElementById('bm-results-context');

    context.textContent = `${REGION_LABELS[inputs.region]} · ${FACILITY_LABELS[inputs.facility_type]} · ${SIZE_LABELS[inputs.company_size]}`;
    kpiGrid.innerHTML = cards.join('');

    // Sources
    const sourcesList = document.getElementById('bm-sources-list');
    const usedSources = new Set();
    Object.values(SOURCE_LABELS).forEach((_, k) => {});
    ['bls_oews_2024','issa_cims_2024','bscai_2024','ibisworld_2025'].forEach(id => {
      const s = SOURCE_LABELS[id];
      if (!s) return;
      const li = document.createElement('li');
      li.innerHTML = `<a href="${s.url}" target="_blank" rel="noopener">${s.name}</a>`;
      sourcesList.appendChild(li);
    });

    formSection.hidden = true;
    resultsSection.hidden = false;
    resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Form handling ──────────────────────────────────────────────
  const form     = document.getElementById('bm-form');
  const submitBtn = document.getElementById('bm-submit');
  const resetBtn  = document.getElementById('bm-reset');

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    // Clear errors
    document.querySelectorAll('.bm-err').forEach(el => el.textContent = '');

    const fd = new FormData(form);
    const inputs = {
      region:       fd.get('region') || '',
      facility_type: fd.get('facility_type') || '',
      company_size: fd.get('company_size') || '',
      bid_rate:     fd.get('bid_rate')   ? parseFloat(fd.get('bid_rate'))   : null,
      win_rate:     fd.get('win_rate')   ? parseFloat(fd.get('win_rate'))   : null,
      margin_pct:   fd.get('margin_pct') ? parseFloat(fd.get('margin_pct')) : null
    };

    // Validate
    let valid = true;
    if (!inputs.region)       { document.getElementById('err-region').textContent   = 'Select your region.'; valid = false; }
    if (!inputs.facility_type){ document.getElementById('err-facility').textContent = 'Select a facility type.'; valid = false; }
    if (!inputs.company_size) { document.getElementById('err-size').textContent     = 'Select your company size.'; valid = false; }
    if (!inputs.bid_rate && !inputs.win_rate && !inputs.margin_pct) {
      document.getElementById('err-rate').textContent = 'Enter at least one metric to benchmark.';
      valid = false;
    }
    if (!valid) return;

    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Analyzing…'; }

    // Try worker endpoint
    let cards = null;
    try {
      if (WORKER_BASE) {
        const res = await fetch(`${WORKER_BASE}/api/benchmarking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(inputs)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.kpi_cards) cards = data.kpi_cards;
        }
      }
    } catch (_) {}

    // Client-side fallback (always runs if no worker response)
    if (!cards) {
      cards = runClientAnalysis(inputs);
    }

    if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> Run benchmarking analysis'; }
    renderResults(inputs, cards);
  });

  resetBtn?.addEventListener('click', () => {
    document.getElementById('bm-results').hidden = true;
    document.querySelector('.bm-section--form').hidden = false;
    document.getElementById('bm-sources-list').innerHTML = '';
    document.querySelector('.bm-section--form').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

})();
